import { LOCALSTORAGE_KEY, orderType } from "../Constants";
import CleverTapService from "./CleverTapService";

const getOtpAttempt = (phone: string) =>
  Number(sessionStorage.getItem(`otp_attempt_${phone}`) || 0);

const incrementOtpAttempt = (phone: string) => {
  const attempt = getOtpAttempt(phone) + 1;
  sessionStorage.setItem(`otp_attempt_${phone}`, String(attempt));
  return attempt;
};

const getVerifyOtpAttempt = (phone: string) => {
  return Number(sessionStorage.getItem(`otp_verify_attempt_${phone}`) || 0);
};

const incrementVerifyOtpAttempt = (phone: string): number => {
  const attempt = getVerifyOtpAttempt(phone) + 1;
  sessionStorage.setItem(`otp_verify_attempt_${phone}`, String(attempt));
  return attempt;
};

const getIsoTimestamp = (): string => {
  return new Date().toISOString();
};

const getOtpSentTimeKey = (phone: string) => {
  return `otp_sent_time_${phone}`;
};

const storeOtpSentTime = (phone: string) => {
  sessionStorage.setItem(getOtpSentTimeKey(phone), String(Date.now()));
};

const getOtpVerificationTimeSeconds = (phone: string): number | null => {
  const sentTime = sessionStorage.getItem(getOtpSentTimeKey(phone));

  if (!sentTime) return null;

  const diffMs = Date.now() - Number(sentTime);
  return Math.round(diffMs / 1000);
};

const clearOtpSentTime = (phone: string) => {
  sessionStorage.removeItem(getOtpSentTimeKey(phone));
};

const formatPhoneNumber = (phone?: string, dialCode?: string): string => {
  if (!phone) return "";

  return `${dialCode ? `+${dialCode}` : ""}${phone}`;
};

type EventData = Record<string, any>;

const trackCleavertapEvent = (eventName: string, data: EventData = {}) => {
  try {
    CleverTapService.trackEvent(eventName, {
      ...data,
      User_ID:
        JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY.USERINFO) ?? "{}")
          ?.id ?? "Guest User",
      Timestamp: getIsoTimestamp(),
      Platform: "web",
    });
  } catch (error) {}
};

function calculateLoginGapInDays(userUpdatedAtIso: string): number {
  const lastLogin = new Date(userUpdatedAtIso).getTime();
  const now = Date.now();

  const diffMs = now - lastLogin;

  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

const getBrowserFromUAData = () => {
  const uaData = (navigator as any).userAgentData;

  if (!uaData?.brands?.length) {
    return { Browser: "Unknown", Browser_Version: "unknown" };
  }

  const preferredOrder = [
    "Google Chrome",
    "Microsoft Edge",
    "Brave",
    "Opera",
    "Vivaldi",
    "Chromium",
  ];

  let selectedBrand = null;

  // 🔑 priority-first matching
  for (const preferred of preferredOrder) {
    const match = uaData.brands.find((b: any) => b.brand === preferred);
    if (match) {
      selectedBrand = match;
      break;
    }
  }

  // fallback (ignore "Not(A:Brand")
  if (!selectedBrand) {
    selectedBrand = uaData.brands.find(
      (b: any) => !b.brand.toLowerCase().includes("not"),
    );
  }

  return {
    Browser: selectedBrand?.brand || "Unknown",
    Browser_Version: selectedBrand?.version || "unknown",
  };
};

const getOSFromUA = () => {
  const ua = navigator.userAgent;

  // macOS
  if (/Mac OS X/i.test(ua)) {
    const match = ua.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
    return {
      OS: "macOS",
      OS_Version: match ? match[1].replace(/_/g, ".") : "unknown",
    };
  }

  // Windows
  if (/Windows NT/i.test(ua)) {
    const match = ua.match(/Windows NT (\d+\.\d+)/);
    const map: Record<string, string> = {
      "10.0": "10 / 11",
      "6.3": "8.1",
      "6.2": "8",
      "6.1": "7",
    };
    return {
      OS: "Windows",
      OS_Version: map[match?.[1] || ""] || "unknown",
    };
  }

  // Android
  if (/Android/i.test(ua)) {
    const match = ua.match(/Android (\d+(\.\d+)?)/);
    return {
      OS: "Android",
      OS_Version: match?.[1] || "unknown",
    };
  }

  // iOS
  if (/iPhone|iPad/i.test(ua)) {
    const match = ua.match(/OS (\d+[._]\d+)/);
    return {
      OS: "iOS",
      OS_Version: match ? match[1].replace(/_/g, ".") : "unknown",
    };
  }

  return { OS: "unknown", OS_Version: "unknown" };
};

const getOSInfo = () => {
  const uaData = (navigator as any).userAgentData;

  if (!uaData) {
    return getOSFromUA();
  }

  const OS = uaData.platform;
  const Device_Type = uaData.mobile ? "mobile" : "desktop";

  let OS_Version = "unknown";

  if (uaData.getHighEntropyValues) {
    return uaData
      .getHighEntropyValues(["platformVersion"])
      .then((values: any) => ({
        OS,
        OS_Version: values?.platformVersion || "unknown",
        Device_Type,
      }))
      .catch(() => ({
        OS,
        OS_Version: "unknown",
        Device_Type,
      }));
  }

  return { OS, OS_Version, Device_Type };
};

const getDeviceTypeFromUserAgent = (): any => {
  const userAgent = navigator.userAgent;
  const ua = userAgent.toLowerCase();

  // 🤖 Bots / Crawlers
  if (
    /bot|crawler|spider|crawling|googlebot|bingbot|yandex|duckduckbot/.test(ua)
  ) {
    return "Bot";
  }

  // 📱 Tablets
  if (/ipad|tablet|playbook|silk|(android(?!.*mobile))/.test(ua)) {
    return "Tablet";
  }

  // 📱 Mobiles
  if (
    /mobile|iphone|ipod|android|blackberry|iemobile|kindle|opera mini/.test(ua)
  ) {
    return "Mobile";
  }

  // 🖥️ Desktop
  if (/windows|macintosh|linux|cros/.test(ua)) {
    return "Desktop";
  }

  return "Unknown";
};

const getExternalEntryPoint = (): string => {
  const referrer = document.referrer || "";
  const currentHost = window.location.host;

  // 1️⃣ Check for UTM / campaign parameters
  const searchParams = new URLSearchParams(window.location.search);
  const utmSource = searchParams.get("utm_source");
  if (utmSource) return `UTM - ${utmSource}`;

  // 2️⃣ Direct / bookmark
  if (!referrer) return "Direct / Bookmark";

  // 3️⃣ Internal navigation within same domain
  if (referrer.includes(currentHost)) return "Internal Navigation";

  // 4️⃣ External social / external sites
  if (referrer.includes("facebook.com")) return "Social - Facebook";
  if (referrer.includes("twitter.com")) return "Social - Twitter";
  if (referrer.includes("instagram.com")) return "Social - Instagram";

  // 5️⃣ Generic external site
  return "External Site";
};

const getNetworkType = () => {
  const nav = navigator as any;
  return nav.connection?.effectiveType || "unknown";
};

const getTotalCartPrice = (cartItems: any[]) => {
  if (!Array.isArray(cartItems)) return 0;

  return cartItems.reduce((total, item) => {
    const price = item?.priceInfo?.finalPrice || 0;
    return total + price;
  }, 0);
};

const getTotalItemCount = (items: any[]) => {
  if (!Array.isArray(items)) return 0;
  return items.reduce((total, item) => {
    return total + (item.count || 0);
  }, 0);
};

const getOrderQuantityClertap = (cartPayload: any): number => {
  if (!cartPayload) return 0;

  const { orderType: type, isSubscribed } = cartPayload;

  if (
    [orderType.pujaPackage, orderType.puja, orderType.chadhavaPackage].includes(
      type,
    )
  ) {
    return 1;
  }

  if (type === orderType.chadhava) {
    return getTotalItemCount(
      isSubscribed
        ? (cartPayload.subscriptionChadhavas ?? [])
        : (cartPayload.selectedChadhavaPackage ?? []),
    );
  }

  if (type === orderType.prasad) {
    return getTotalItemCount(cartPayload.selectedPrasadPackage ?? []);
  }

  return 0;
};

const sortByOrderBookingDateDesc = (data: any[]): any[] => {
  return [...data].sort(
    (a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime(),
  );
};

const getDaysFromTodayIST = (inputDate?: string | null): number => {
  if (!inputDate) return 0;

  const IST_OFFSET = 5.5 * 60 * 60 * 1000;

  const now = new Date();
  const todayIST = new Date(now.getTime() + IST_OFFSET);

  const parsedDate = new Date(inputDate);

  if (isNaN(parsedDate.getTime())) return 0;

  const givenDateIST = new Date(parsedDate.getTime() + IST_OFFSET);

  todayIST.setHours(0, 0, 0, 0);
  givenDateIST.setHours(0, 0, 0, 0);

  const diffMs = givenDateIST.getTime() - todayIST.getTime();

  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

const convertApiTimeToDate = (apiTime: string) => {
  if (!apiTime) return null;
  const [time, meridian] = apiTime?.split(" ");

  const [hourStr, minuteStr] = time?.split(":");

  let hour = parseInt(hourStr);
  const minute = parseInt(minuteStr);

  if (meridian === "pm" && hour < 12) hour += 12;
  if (meridian === "am" && hour === 12) hour = 0;

  const now = new Date();

  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hour,
    minute,
    0,
  );
};

export {
  getOtpAttempt,
  incrementOtpAttempt,
  getIsoTimestamp,
  getOtpSentTimeKey,
  storeOtpSentTime,
  getOtpVerificationTimeSeconds,
  clearOtpSentTime,
  formatPhoneNumber,
  trackCleavertapEvent,
  getVerifyOtpAttempt,
  incrementVerifyOtpAttempt,
  calculateLoginGapInDays,
  getBrowserFromUAData,
  getOSFromUA,
  getOSInfo,
  getDeviceTypeFromUserAgent,
  getExternalEntryPoint,
  getNetworkType,
  getTotalCartPrice,
  getTotalItemCount,
  getOrderQuantityClertap,
  sortByOrderBookingDateDesc,
  getDaysFromTodayIST,
  convertApiTimeToDate,
};
